import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Form, Input, Popconfirm, Spin } from 'antd';
import { useParams } from 'react-router';

import { useDocumentType } from '../hooks';
import { Divider, NestedBox, ActionBox, Box } from '../styled';
import { LoadingOutlined } from '@ant-design/icons';
import { ConstructorForm } from '@/components/admin/molecules';

const { TextArea } = Input;

export const DocumentType = ({ sectionProp, onCreated }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [section, setSection] = useState(sectionProp || {});
  const {
    handleCreate,
    handleUpdate,
    handleDelete,
    handleChangeRole,
    handleChangeIsRequired,
    isCreating,
    isUpdating,
    isDeleting,
    isChangingRole,
    isChangingIsRequired
  } = useDocumentType({ kycDocumentSectionId: id });

  useEffect(() => {
    if (!sectionProp?.id) return;

    setSection(sectionProp);
  }, [sectionProp]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setSection({ ...section, [name]: value });
  };

  const onChangeRole = (e) => {
    const { name, checked } = e.target;

    handleChangeRole(name, checked, section);
  };
  const onChangeIsRequired = (e) => {
    const { name, checked } = e.target;

    handleChangeIsRequired(name, checked, section);
  };

  const onChangeRolesWithAccess = (role, checked) => {
    const currentRoles = section.rolesWithAccess || [];

    let newRoles;
    if (checked) {
      newRoles = [...currentRoles, role];
    } else {
      newRoles = currentRoles.filter((r) => r !== role);
    }

    setSection({ ...section, rolesWithAccess: newRoles });
  };

  const handleAction = () => {
    const defaultRoles = ['COMPANY', 'PERSONAL', 'AGENT', 'RECOMMENDER'];
    const sectionToSave = {
      ...section,
      rolesWithAccess:
        section.rolesWithAccess !== undefined && section.rolesWithAccess !== null
          ? section.rolesWithAccess.length > 0
            ? section.rolesWithAccess
            : defaultRoles
          : defaultRoles
    };

    if (section.id) {
      handleUpdate(sectionToSave);
    } else {
      handleCreate(sectionToSave).then((res) => {
        if (res?.data?.id) {
          setSection({});
          onCreated && onCreated();
        }
      });
    }
  };

  const onDelete = () => {
    handleDelete(section);
  };

  // const isDisabled = !section.id || (
  //   section.name === sectionProp?.name
  // );
  const isDisabled = !section.name;

  return (
    <Box
      $isNew={!section.id}
      styles={{
        body: {
          padding: '16px'
        }
      }}
    >
      <ConstructorForm>
        <Form.Item label={t('Name')}>
          <Input name='name' value={section.name} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Name')} (RU)`}>
          <Input name='name_RU' value={section.name_RU} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Name')} (LV)`}>
          <Input name='name_LV' value={section.name_LV} onChange={onChange} />
        </Form.Item>
        <Form.Item label={t('Title')}>
          <Input name='title' value={section.title} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Title')} (RU)`}>
          <Input name='title_RU' value={section.title_RU} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Title')} (LV)`}>
          <Input name='title_LV' value={section.title_LV} onChange={onChange} />
        </Form.Item>
        <Form.Item label={t('Expiration Text')}>
          <Input name='expirationText' value={section.expirationText} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Expiration Text')} (RU)`}>
          <Input name='expirationText_RU' value={section.expirationText_RU} onChange={onChange} />
        </Form.Item>
        <Form.Item label={`${t('Expiration Text')} (LV)`}>
          <Input name='expirationText_LV' value={section.expirationText_LV} onChange={onChange} />
        </Form.Item>
        <Form.Item label={t('Expiration Months')}>
          <Input
            name='expirationMonths'
            type='number'
            value={section.expirationMonths}
            onChange={onChange}
          />
        </Form.Item>
        <Form.Item label={t('Descriptions')}>
          <TextArea name='descriptions' value={section.descriptions} onChange={onChange} rows={4} />
        </Form.Item>
        <Form.Item label={`${t('Descriptions')} (RU)`}>
          <TextArea
            name='descriptions_RU'
            value={section.descriptions_RU}
            onChange={onChange}
            rows={4}
          />
        </Form.Item>
        <Form.Item label={`${t('Descriptions')} (LV)`}>
          <TextArea
            name='descriptions_LV'
            value={section.descriptions_LV}
            onChange={onChange}
            rows={4}
          />
        </Form.Item>

        <div style={{ display: 'inline-block' }}>
          <Form.Item label={t('Roles')}>
            <Spin indicator={<LoadingOutlined spin />} spinning={isChangingRole}>
              <Checkbox
                name='PERSONAL'
                checked={section?.roles?.some((role) => role.role === 'PERSONAL')}
                onChange={onChangeRole}
                style={{ marginRight: '35px' }}
              >
                {t('PERSONAL')}
              </Checkbox>
              <Checkbox
                name='COMPANY'
                checked={section?.roles?.some((role) => role.role === 'COMPANY')}
                onChange={onChangeRole}
                style={{ marginRight: '35px' }}
              >
                {t('COMPANY')}
              </Checkbox>
              <Checkbox
                name='AGENT'
                checked={section?.roles?.some((role) => role.role === 'AGENT')}
                onChange={onChangeRole}
                style={{ marginRight: '35px' }}
              >
                {t('AGENT')}
              </Checkbox>
              <Checkbox
                name='RECOMMENDER'
                checked={section?.roles?.some((role) => role.role === 'RECOMMENDER')}
                onChange={onChangeRole}
                style={{ marginRight: '35px' }}
              >
                {t('ASSISTANT')}
              </Checkbox>
            </Spin>
          </Form.Item>
          <Form.Item label={t('Required')}>
            <Spin indicator={<LoadingOutlined spin />} spinning={isChangingIsRequired}>
              <Checkbox
                name='PERSONAL'
                checked={section?.roles?.some(
                  (role) => role.role === 'PERSONAL' && role.isRequired === true
                )}
                onChange={onChangeIsRequired}
                style={{ marginRight: '35px' }}
              >
                {t('PERSONAL')}
              </Checkbox>
              <Checkbox
                name='COMPANY'
                checked={section?.roles?.some(
                  (role) => role.role === 'COMPANY' && role.isRequired === true
                )}
                onChange={onChangeIsRequired}
                style={{ marginRight: '35px' }}
              >
                {t('COMPANY')}
              </Checkbox>
              <Checkbox
                name='AGENT'
                checked={section?.roles?.some(
                  (role) => role.role === 'AGENT' && role.isRequired === true
                )}
                onChange={onChangeIsRequired}
                style={{ marginRight: '35px' }}
              >
                {t('AGENT')}
              </Checkbox>
              <Checkbox
                name='RECOMMENDER'
                checked={section?.roles?.some(
                  (role) => role.role === 'RECOMMENDER' && role.isRequired === true
                )}
                onChange={onChangeIsRequired}
                style={{ marginRight: '35px' }}
              >
                {t('ASSISTANT')}
              </Checkbox>
            </Spin>
          </Form.Item>
          <Form.Item label={t('Roles with Access')}>
            <Checkbox
              checked={section?.rolesWithAccess?.includes('COMPANY')}
              onChange={(e) => onChangeRolesWithAccess('COMPANY', e.target.checked)}
              style={{ marginRight: '35px' }}
            >
              {t('COMPANY')}
            </Checkbox>
            <Checkbox
              checked={section?.rolesWithAccess?.includes('PERSONAL')}
              onChange={(e) => onChangeRolesWithAccess('PERSONAL', e.target.checked)}
              style={{ marginRight: '35px' }}
            >
              {t('PERSONAL')}
            </Checkbox>
            <Checkbox
              checked={section?.rolesWithAccess?.includes('AGENT')}
              onChange={(e) => onChangeRolesWithAccess('AGENT', e.target.checked)}
              style={{ marginRight: '35px' }}
            >
              {t('AGENT')}
            </Checkbox>
            <Checkbox
              checked={section?.rolesWithAccess?.includes('RECOMMENDER')}
              onChange={(e) => onChangeRolesWithAccess('RECOMMENDER', e.target.checked)}
              style={{ marginRight: '35px' }}
            >
              {t('ASSISTANT')}
            </Checkbox>
          </Form.Item>
        </div>

        <ActionBox>
          <Button
            onClick={handleAction}
            disabled={isDisabled}
            type='primary'
            loading={isCreating || isUpdating}
          >
            {section.id ? t('Update Document Type') : t('Create Document Type')}
          </Button>

          {section.id && (
            <Popconfirm
              placement='topRight'
              title={t('Are you sure to delete this document type?')}
              onConfirm={onDelete}
              okText={t('Yes')}
              cancelText={t('No')}
            >
              <Button danger loading={isDeleting}>
                {t('Delete Document Type')}
              </Button>
            </Popconfirm>
          )}
        </ActionBox>

        <NestedBox>{section.id && <Divider />}</NestedBox>
      </ConstructorForm>
    </Box>
  );
};
